import React, { useEffect, useRef, useState, useCallback } from 'react';
import { loadMapsLibrary } from '@/lib/googleMaps';
import { api } from '@/lib/api';
import { BookingDialog } from '@/components/public/BookingDialog';
import { PoiDialog } from '@/components/public/PoiDialog';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Compass, MapPin, Clock, Ruler, Mountain, CalendarCheck, Loader2, Layers, Satellite, Undo2, Play, Info, ChevronDown, Route } from 'lucide-react';

// Vista panoramica iniziale: Lago Cecita / cuore della Sila
const OVERVIEW = { lat: 39.375, lng: 16.52, range: 14000, tilt: 55, heading: 20, altitude: 0 };
const LANDING_BG = 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1920&q=80';

// Riporta al CRM l'esito REALE del caricamento mappa (per pannello diagnostica + notifiche admin).
const reportMapHealth = (status, error_code = '', message = '') => {
  try { api.post('/parco/map-health', { status, error_code, message }).catch(() => {}); } catch (e) { /* noop */ }
};

// coordinate [{lat,lng}] da un'area (geojson LineString o punti)
const coordsOf = (area) => {
  const g = area.geojson && (area.geojson.type === 'Feature' ? area.geojson.geometry : area.geojson);
  if (g && g.type === 'LineString') return g.coordinates.map((c) => ({ lat: c[1], lng: c[0] }));
  if (g && g.type === 'MultiLineString') return g.coordinates.flat().map((c) => ({ lat: c[1], lng: c[0] }));
  const pts = (area.points || []).filter((p) => p.lat != null && p.lng != null);
  return pts.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
};
const centroid = (cs) => cs.length ? { lat: cs.reduce((s, c) => s + c.lat, 0) / cs.length, lng: cs.reduce((s, c) => s + c.lng, 0) / cs.length } : null;

// Perimetro APPROSSIMATIVO del Parco Nazionale della Sila (lat/lng)
const PARK_PERIMETER = [
  [39.420, 16.415], [39.445, 16.510], [39.430, 16.610], [39.375, 16.710],
  [39.300, 16.775], [39.215, 16.780], [39.120, 16.730], [39.055, 16.640],
  [39.030, 16.520], [39.070, 16.415], [39.160, 16.350], [39.270, 16.345],
  [39.360, 16.375], [39.420, 16.415],
].map(([lat, lng]) => ({ lat, lng, altitude: 0 }));

// ---- PERFORMANCE: semplificazione tracciati (best practice Google Maps 3D) ----
// I KMZ CAI hanno anche 1000+ punti: senza semplificazione la mappa 3D si blocca.
// Distanza perpendicolare punto-segmento (in gradi lat/lng, sufficiente per la scala).
const _perpDist = (p, a, b) => {
  const x = p.lng, y = p.lat, x1 = a.lng, y1 = a.lat, x2 = b.lng, y2 = b.lat;
  const dx = x2 - x1, dy = y2 - y1;
  if (dx === 0 && dy === 0) return Math.hypot(x - x1, y - y1);
  let t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
};
// Ramer–Douglas–Peucker (iterativo, per evitare stack overflow su tracciati lunghi).
const _rdp = (pts, eps) => {
  if (pts.length < 3) return pts.slice();
  const keep = new Array(pts.length).fill(false);
  keep[0] = keep[pts.length - 1] = true;
  const stack = [[0, pts.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop();
    let dmax = 0, idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = _perpDist(pts[i], pts[s], pts[e]);
      if (d > dmax) { dmax = d; idx = i; }
    }
    if (dmax > eps && idx !== -1) { keep[idx] = true; stack.push([s, idx], [idx, e]); }
  }
  return pts.filter((_, i) => keep[i]);
};
// Cap duro sul numero di vertici (stride uniforme).
const _cap = (pts, max) => {
  if (pts.length <= max) return pts;
  const step = Math.ceil(pts.length / max);
  const out = [];
  for (let i = 0; i < pts.length; i += step) out.push(pts[i]);
  if (out[out.length - 1] !== pts[pts.length - 1]) out.push(pts[pts.length - 1]);
  return out;
};
// eps ~0.000045 gradi (~5 m): alta fedelta' (tornanti preservati). Allineato al backend.
const simplifyPath = (cs, { eps = 0.000045, max = 300 } = {}) => _cap(_rdp(_cap(cs, 900), eps), max);

// Puntini "mappa del tesoro" campionati per distanza cumulativa, con tetto massimo.
const dotsAlong = (cs, maxDots = 10) => {
  if (cs.length <= 2) return cs.slice();
  const cum = [0];
  for (let i = 1; i < cs.length; i++) cum[i] = cum[i - 1] + Math.hypot(cs[i].lat - cs[i - 1].lat, cs[i].lng - cs[i - 1].lng);
  const total = cum[cum.length - 1];
  if (total === 0) return [cs[0]];
  const n = Math.max(2, Math.min(maxDots, cs.length));
  const out = [];
  for (let k = 0; k < n; k++) {
    const target = (total * k) / (n - 1);
    let i = 1; while (i < cs.length - 1 && cum[i] < target) i++;
    const denom = cum[i] - cum[i - 1];
    const t = denom ? (target - cum[i - 1]) / denom : 0;
    out.push({ lat: cs[i - 1].lat + (cs[i].lat - cs[i - 1].lat) * t, lng: cs[i - 1].lng + (cs[i].lng - cs[i - 1].lng) * t });
  }
  return out;
};

// Esegue una coda di task di creazione elementi a "chunk" (non blocca il thread).
const runChunked = (tasks, perFrame = 30) => {
  let i = 0;
  const step = () => {
    const end = Math.min(i + perFrame, tasks.length);
    for (; i < end; i++) { try { tasks[i](); } catch (e) { /* noop */ } }
    if (i < tasks.length) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

export const SilaWorld3D = () => {
  const wrapRef = useRef(null);
  const mapRef = useRef(null);
  const libRef = useRef(null);
  const areasRef = useRef([]);
  const drawnRef = useRef(false);
  const watchdogRef = useRef(null);
  const [areas, setAreas] = useState([]);
  const [phase, setPhase] = useState('landing'); // landing | loading | flying | ready | error
  const [errMsg, setErrMsg] = useState('');
  const [mode, setMode] = useState('HYBRID');
  const [activeId, setActiveId] = useState(null);
  const [booking, setBooking] = useState({ open: false, area: null });
  const [poi, setPoi] = useState({ open: false, point: null, area: null });
  const [openItin, setOpenItin] = useState(true);
  const [openLuoghi, setOpenLuoghi] = useState(false);

  useEffect(() => {
    api.get('/parco/public/areas').then(({ data }) => { setAreas(data || []); areasRef.current = data || []; }).catch(() => {});
  }, []);

  const drawAreas = useCallback((map, lib) => {
    if (drawnRef.current) return; // evita doppio rendering (animationend + safety timeout)
    drawnRef.current = true;
    reportMapHealth('ok'); // caricamento 3D riuscito
    const { Polyline3DElement, Polygon3DElement, Marker3DElement, Marker3DInteractiveElement, AltitudeMode } = lib.maps3d;
    const PinElement = lib.marker && lib.marker.PinElement;

    // 1) Perimetro del Parco (area circoscritta)
    if (Polygon3DElement) {
      try {
        const poly = new Polygon3DElement({
          path: PARK_PERIMETER,
          altitudeMode: AltitudeMode.CLAMP_TO_GROUND,
          fillColor: 'rgba(57,217,138,0.12)',
          strokeColor: '#39d98a',
          strokeWidth: 4,
          drawsOccludedSegments: true,
        });
        map.append(poly);
      } catch (e) { /* noop */ }
    }

    // 2) Sentieri (linea semplificata + puntini "mappa del tesoro" con cap + pin punti)
    // Tutta la creazione elementi viene accodata ed eseguita a chunk per non bloccare il volo.
    const tasks = [];
    const list = areasRef.current;
    // con molti itinerari riduciamo ulteriormente i puntini per tenere leggera la scena 3D
    const maxDots = list.length > 15 ? 8 : 12;

    list.forEach((area) => {
      const isLuogo = area.tipo === 'luogo';
      const csRaw = coordsOf(area);
      const color = area.color || '#c9862a';
      if (!isLuogo && csRaw.length >= 2 && Polyline3DElement) {
        const cs = simplifyPath(csRaw);
        tasks.push(() => {
          const glow = new Polyline3DElement({
            path: cs.map((c) => ({ ...c, altitude: 6 })),
            altitudeMode: AltitudeMode.RELATIVE_TO_GROUND,
            strokeColor: color,
            strokeWidth: 10,
            drawsOccludedSegments: false, // OFF: l'occlusione e' costosa (best practice Google)
          });
          map.append(glow);
        });
        if (Marker3DElement && PinElement) {
          dotsAlong(cs, maxDots).forEach((d) => {
            tasks.push(() => {
              const pin = new PinElement({ background: '#fffdf5', borderColor: color, glyphColor: color, scale: 0.42 });
              const dot = new Marker3DElement({ position: { ...d, altitude: 4 }, altitudeMode: AltitudeMode.RELATIVE_TO_GROUND });
              dot.append(pin);
              map.append(dot);
            });
          });
        }
      }
      // punti cliccabili: itinerario = pin colore area; luogo = pin giallo con "?"
      (area.points || []).forEach((p) => {
        if (p.lat == null || p.lng == null || !Marker3DInteractiveElement) return;
        tasks.push(() => {
          const m = new Marker3DInteractiveElement({ position: { lat: Number(p.lat), lng: Number(p.lng), altitude: 45 }, altitudeMode: AltitudeMode.RELATIVE_TO_GROUND, label: p.name || area.name });
          if (PinElement) {
            try {
              const pin = isLuogo
                ? new PinElement({ background: '#f5c518', borderColor: '#ffffff', glyphColor: '#3a2d00', glyphText: '?', scale: 1.25 })
                : new PinElement({ background: color, borderColor: '#ffffff', glyphColor: '#ffffff', scale: 1.15 });
              m.append(pin);
            } catch (e) { /* noop */ }
          }
          m.addEventListener('gmp-click', () => setPoi({ open: true, point: p, area }));
          map.append(m);
        });
      });
    });

    runChunked(tasks.map(task => () => {
      if (mapRef.current === map && map.isConnected) task();
    }), 25);
  }, []);

  const flyToArea = useCallback((area) => {
    const map = mapRef.current;
    if (!map) return;
    const c = centroid(coordsOf(area));
    if (!c) return;
    setActiveId(area.id);
    try {
      map.flyCameraTo({ endCamera: { center: { lat: c.lat, lng: c.lng, altitude: 60 }, range: 1400, tilt: 66, heading: 20 }, durationMillis: 2200 });
    } catch (e) { /* noop */ }
  }, []);

  const resetView = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    setActiveId(null);
    try { map.flyCameraTo({ endCamera: { center: { lat: OVERVIEW.lat, lng: OVERVIEW.lng, altitude: 0 }, range: OVERVIEW.range, tilt: OVERVIEW.tilt, heading: OVERVIEW.heading }, durationMillis: 2200 }); } catch (e) { /* noop */ }
  }, []);


  // Ogni avvio ha un identificatore: risultati tardivi non riattivano mappe obsolete.
  const attemptRef = useRef(0);
  const startingRef = useRef(false);
  const cleanupRef = useRef(null);

  const clearMap = useCallback(() => {
    attemptRef.current += 1;
    startingRef.current = false;
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = null;
    if (cleanupRef.current) cleanupRef.current();
    cleanupRef.current = null;
    const map = mapRef.current;
    mapRef.current = null;
    if (map) {
      try {
        Promise.resolve(map.stopCameraAnimation?.()).catch(() => {});
      } catch {}
      map.remove();
    }
    drawnRef.current = false;
  }, []);

  useEffect(() => () => clearMap(), [clearMap]);

  const enter = useCallback(async () => {
    if (startingRef.current) return;
    clearMap();
    const attempt = attemptRef.current;
    startingRef.current = true;
    setErrMsg('');
    setPhase('loading');

    const current = () => attemptRef.current === attempt;
    const fail = (code, message) => {
      if (!current()) return;
      clearMap();
      const safeMessage = String(message || '')
        .replace(/AIza[A-Za-z0-9_-]+/g, '[key]');
      setErrMsg(safeMessage);
      setPhase('error');
      reportMapHealth('error', code, safeMessage);
    };

    // Include anche il tempo impiegato dal loader Google.
    watchdogRef.current = setTimeout(() => {
      fail('startup_timeout', 'Avvio 3D non completato entro 30 secondi.');
    }, 30000);

    try {
      if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        fail('no_key', 'Chiave Google Maps non configurata.');
        return;
      }
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2');
      if (!gl) {
        fail('webgl_unsupported', 'WebGL2 non disponibile nel browser.');
        return;
      }
      gl.getExtension('WEBGL_lose_context')?.loseContext();

      const previousAuthFailure = window.gm_authFailure;
      const authFailure = () => fail(
        'auth_failure', 'Autenticazione Google Maps non riuscita.'
      );
      window.gm_authFailure = authFailure;
      const handlers = [];
      let flyTimer;
      let markerTimer;

      cleanupRef.current = () => {
        clearTimeout(flyTimer);
        clearTimeout(markerTimer);
        for (const [element, event, fn] of handlers) {
          element.removeEventListener(event, fn);
        }
        if (window.gm_authFailure === authFailure) {
          window.gm_authFailure = previousAuthFailure;
        }
      };

      const maps3d = await loadMapsLibrary('maps3d');
      if (!current()) return;
      if (typeof maps3d.Map3DElement !== 'function') {
        throw new Error('Motore Google Maps 3D non disponibile.');
      }

      // I marker sono opzionali: non devono bloccare l'avvio della mappa.
      let marker = null;
      try {
        marker = await Promise.race([
          loadMapsLibrary('marker'),
          new Promise(resolve => {
            markerTimer = setTimeout(() => resolve(null), 2500);
          })
        ]);
      } catch {}
      clearTimeout(markerTimer);
      if (!current()) return;

      const lib = { maps3d, marker };
      libRef.current = lib;
      const map = new maps3d.Map3DElement({
        center: { lat: OVERVIEW.lat, lng: OVERVIEW.lng, altitude: 0 },
        range: 20000000, tilt: 0, heading: 0, mode
      });
      map.style.width = '100%';
      map.style.height = '100%';
      mapRef.current = map;
      let steady = false;
      let ready = false;

      const atDestination = () => {
        const c = map.center;
        return c &&
          Math.abs(Number(c.lat) - OVERVIEW.lat) < 0.02 &&
          Math.abs(Number(c.lng) - OVERVIEW.lng) < 0.02 &&
          Number(map.range) <= OVERVIEW.range * 1.5;
      };

      const finish = () => {
        if (!current() || ready || !steady || !atDestination()) return;
        ready = true;
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
        startingRef.current = false;
        setPhase('ready');
        drawAreas(map, lib);
      };

      const listen = (event, fn) => {
        map.addEventListener(event, fn);
        handlers.push([map, event, fn]);
      };
      listen('gmp-error', () => {
        fail('map_initialization_error', 'Google non ha inizializzato la scena 3D.');
      });
      listen('gmp-steadychange', event => {
        steady = event.isSteady === true;
        finish();
      });
      listen('gmp-animationend', finish);

      if (!wrapRef.current || !current()) {
        clearMap();
        return;
      }
      wrapRef.current.appendChild(map);
      setPhase('flying');

      const destination = {
        center: {
          lat: OVERVIEW.lat, lng: OVERVIEW.lng,
          altitude: OVERVIEW.altitude
        },
        range: OVERVIEW.range, tilt: OVERVIEW.tilt,
        heading: OVERVIEW.heading
      };

      flyTimer = setTimeout(async () => {
        if (!current()) return;
        try {
          await map.flyCameraTo({
            endCamera: destination, durationMillis: 4200
          });
        } catch {
          if (!current()) return;
          // Se fallisce solo il volo, prova la vista finale senza animazione.
          try {
            steady = false;
            map.center = destination.center;
            map.range = destination.range;
            map.tilt = destination.tilt;
            map.heading = destination.heading;
          } catch {
            fail('camera_error', 'Impossibile posizionare la visuale 3D.');
          }
        }
      }, 400);
    } catch (e) {
      fail('load_error', e?.message || 'Caricamento Google Maps non riuscito.');
    }
  }, [mode, clearMap, drawAreas]);

  // toggle satellite/ibrido
  useEffect(() => {
    if (mapRef.current) { try { mapRef.current.mode = mode; } catch (e) { /* noop */ } }
  }, [mode]);

  const activeArea = areas.find((a) => a.id === activeId);
  const itineraries = areas.filter((a) => a.tipo !== 'luogo');
  const luoghi = areas.filter((a) => a.tipo === 'luogo');

  const openLuogo = (area) => {
    flyToArea(area);
    const p = (area.points || [])[0];
    if (p) setTimeout(() => setPoi({ open: true, point: p, area }), 900);
  };

  return (
    <div className="sila-public relative h-[calc(100dvh-4rem)] w-full overflow-hidden bg-black" data-testid="sila-world-3d">
      <div ref={wrapRef} className="absolute inset-0" />

      {/* LANDING (carica su intento) */}
      {phase === 'landing' && (
        <div
          className="sila-hero-glow absolute inset-0 z-[10] flex items-center justify-center overflow-hidden"
          data-testid="parchi-landing"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            e.currentTarget.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
            e.currentTarget.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
          }}
        >
          <img src={LANDING_BG} alt="Parco della Sila" className="sila-kb-loop absolute inset-0 h-full w-full object-cover" />
          <div aria-hidden className="absolute inset-0 z-[1]" style={{ background: 'linear-gradient(180deg, hsl(150 42% 8% / 0.5) 0%, hsl(150 42% 8% / 0.35) 45%, hsl(150 42% 8% / 0.85) 100%)' }} />
          {/* bagliore caldo "sole tra i pini" che segue il cursore (sopra il gradiente) */}
          <div aria-hidden className="sila-sunlight" style={{ zIndex: 2 }} />
          <div className="relative z-[3] mx-auto max-w-2xl px-6 text-center text-white">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] backdrop-blur-md">
              <Compass className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />Parco Nazionale della Sila
            </span>
            <h1 className="mt-5 font-display text-5xl font-bold leading-[1.03] drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)] sm:text-6xl">Il Parco della Sila<br />come non l'hai mai visto</h1>
            <p className="mx-auto mt-5 max-w-xl text-lg text-white/85">Un volo cinematografico sul cuore verde della Calabria. Esplora i sentieri reali e prenota il tuo tour guidato.</p>
            <div className="mt-8 flex flex-col items-center gap-5">
              <Button size="lg" className="rounded-full px-8 text-base" onClick={enter} data-testid="parchi-enter-3d">
                <Play className="mr-2 h-5 w-5" />Esplora il Parco
              </Button>
              <div className="inline-flex max-w-md flex-col items-center gap-0.5 rounded-2xl border border-white/25 bg-white/10 px-5 py-3 text-center backdrop-blur-md" data-testid="parco-mab-badge">
                <span className="font-display text-sm font-bold uppercase tracking-wide text-white">Riserva MAB UNESCO</span>
                <span className="text-xs text-white/85">Decima Area Italiana al Mondo per la sua biodiversità</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LOADING / FLYING */}
      {(phase === 'loading' || phase === 'flying') && (
        <div className="pointer-events-none absolute inset-0 z-[8] flex flex-col items-center justify-center gap-3 bg-black/30 text-white">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">{phase === 'loading' ? 'Avvio del motore 3D…' : 'In volo sulla Sila…'}</p>
        </div>
      )}

      {/* ERROR — fallback elegante con panoramica del Parco */}
      {phase === 'error' && (
        <div className="absolute inset-0 z-[10] flex items-center justify-center overflow-hidden" data-testid="parchi-error">
          <img src={LANDING_BG} alt="Parco Nazionale della Sila" className="sila-kb-loop absolute inset-0 h-full w-full object-cover" />
          <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, hsl(150 42% 8% / 0.55) 0%, hsl(150 42% 8% / 0.45) 45%, hsl(150 42% 8% / 0.9) 100%)' }} />
          <div className="relative z-[3] mx-auto max-w-xl px-6 text-center text-white">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] backdrop-blur-md">
              <Compass className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />Parco Nazionale della Sila
            </span>
            <h2 className="mt-5 font-display text-4xl font-bold leading-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)] sm:text-5xl">Il cuore verde della Calabria</h2>
            <p className="mx-auto mt-4 max-w-md text-base text-white/85">Riserva MAB UNESCO tra laghi, altopiani e pini secolari. La visuale 3D interattiva sarà disponibile a breve.</p>
            <div className="mt-7">
              <Button variant="secondary" className="rounded-full px-7" onClick={() => { clearMap(); setErrMsg(''); setPhase('landing'); }} data-testid="parchi-retry">
                <Play className="mr-2 h-4 w-4" />Riprova la visuale 3D
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* UI INTERATTIVA (dopo l'atterraggio) */}
      {phase === 'ready' && (
        <>
          {/* Controlli in alto */}
          <div className="absolute left-1/2 top-4 z-[6] flex -translate-x-1/2 items-center gap-1 rounded-full bg-background/85 p-1 shadow backdrop-blur">
            <button type="button" onClick={() => setMode('SATELLITE')} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${mode === 'SATELLITE' ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`} data-testid="mode-satellite"><Satellite className="h-3.5 w-3.5" />Satellite</button>
            <button type="button" onClick={() => setMode('HYBRID')} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${mode === 'HYBRID' ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`} data-testid="mode-hybrid"><Layers className="h-3.5 w-3.5" />Ibrido</button>
          </div>

          {/* Pannello a tendina (ordinato) */}
          <div className="absolute left-4 top-4 z-[6] flex max-h-[calc(100%-9rem)] w-[340px] max-w-[calc(100%-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-background/85 shadow-lg backdrop-blur-md" data-testid="route-panel">
            {/* Header / reset */}
            <button type="button" onClick={resetView} className="flex w-full shrink-0 items-center justify-between border-b border-border px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted/60" data-testid="reset-view">
              <span className="inline-flex items-center gap-2"><Undo2 className="h-4 w-4 text-primary" />Panoramica Parco</span>
              <span className="text-xs font-normal text-muted-foreground">{itineraries.length + luoghi.length} punti</span>
            </button>

            <div className="flex-1 overflow-y-auto p-2">
              {/* SEZIONE ITINERARI */}
              <Collapsible open={openItin} onOpenChange={setOpenItin}>
                <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-xl px-2 py-2 text-left transition-colors hover:bg-muted/60" data-testid="toggle-itinerari">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground"><Route className="h-4 w-4 text-primary" />Itinerari<span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{itineraries.length}</span></span>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${openItin ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                  <div className="flex flex-col gap-2 px-1 pb-2 pt-1">
                    {itineraries.map((area) => (
                      <button key={area.id} type="button" onClick={() => flyToArea(area)} data-testid={`route-card-${area.id}`}
                        className={`group flex items-stretch gap-3 overflow-hidden rounded-2xl border bg-background/90 p-2 text-left shadow-sm transition-transform duration-200 hover:-translate-y-0.5 ${activeId === area.id ? 'border-primary ring-2 ring-primary/40' : 'border-border'}`}>
                        <span className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-muted">
                          {area.cover_image ? <img src={area.cover_image} alt={area.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" /> : null}
                          <span className="absolute inset-x-0 bottom-0 h-1.5" style={{ background: area.color || '#39d98a' }} />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col justify-center">
                          <span className="truncate font-display text-sm font-semibold text-foreground">{area.name}</span>
                          {area.guide?.display_name ? <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">{area.guide.photo ? <img src={area.guide.photo} alt="" className="h-4 w-4 rounded-full object-cover" /> : null}<span className="truncate">Guida: {area.guide.display_name}</span></span> : null}
                          <span className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] text-muted-foreground">
                            {area.duration ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{area.duration}</span> : null}
                            {area.length_km ? <span className="inline-flex items-center gap-1"><Ruler className="h-3 w-3" />{area.length_km} km</span> : null}
                          </span>
                          <span className="mt-1.5 flex items-center justify-between">
                            {area.price != null ? <span className="text-xs font-semibold text-foreground">€{area.price}<span className="font-normal text-muted-foreground">/pers.</span></span> : <span />}
                            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setBooking({ open: true, area }); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setBooking({ open: true, area }); } }} data-testid={`route-book-${area.id}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground hover:opacity-90"><CalendarCheck className="h-3 w-3" />Prenota</span>
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* SEZIONE LUOGHI DEL PARCO */}
              {luoghi.length > 0 && (
                <Collapsible open={openLuoghi} onOpenChange={setOpenLuoghi} className="mt-1 border-t border-border pt-1">
                  <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-xl px-2 py-2 text-left transition-colors hover:bg-muted/60" data-testid="toggle-luoghi">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold text-[#3a2d00]" style={{ background: '#f5c518' }}>?</span>
                      Luoghi del Parco<span className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-[#7a5c00]" style={{ background: 'rgba(245,197,24,0.20)' }}>{luoghi.length}</span>
                    </span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${openLuoghi ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="flex flex-col gap-1.5 px-1 pb-2 pt-1">
                      {luoghi.map((area) => (
                        <button key={area.id} type="button" onClick={() => openLuogo(area)} data-testid={`luogo-card-${area.id}`}
                          className={`group flex items-center gap-3 overflow-hidden rounded-xl border bg-background/90 p-1.5 text-left shadow-sm transition-transform duration-200 hover:-translate-y-0.5 ${activeId === area.id ? 'ring-2 ring-[#f5c518]' : 'border-border'}`}
                          style={activeId === area.id ? { borderColor: '#f5c518' } : undefined}>
                          <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-muted">
                            {(area.points?.[0]?.photo || area.cover_image) ? <img src={area.points?.[0]?.photo || area.cover_image} alt={area.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" /> : <span className="flex h-full items-center justify-center text-sm font-bold text-[#3a2d00]" style={{ background: '#f5c518' }}>?</span>}
                          </span>
                          <span className="truncate font-display text-[13px] font-semibold text-foreground">{area.name}</span>
                        </button>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          </div>

          {/* Etichetta itinerario attivo */}
          {activeArea && (
            <div className="sila-glass absolute bottom-5 left-4 z-[6] max-w-[300px] rounded-[var(--radius-lg)] p-3" data-testid="active-chapter">
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: activeArea.tipo === 'luogo' ? '#f5c518' : (activeArea.color || 'hsl(var(--primary))') }} /><span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{activeArea.tipo === 'luogo' ? 'Luogo del Parco' : (activeArea.subtitle || 'Itinerario')}</span></div>
              <p className="mt-1 font-display text-lg font-semibold leading-tight text-foreground">{activeArea.name}</p>
              {activeArea.tipo === 'luogo' ? (
                (activeArea.points || [])[0] ? (
                  <Button size="sm" variant="secondary" className="mt-2 rounded-full" onClick={() => setPoi({ open: true, point: activeArea.points[0], area: activeArea })}><MapPin className="mr-1.5 h-3.5 w-3.5" />Scopri il luogo</Button>
                ) : null
              ) : (
                <Button size="sm" className="mt-2 rounded-full" onClick={() => setBooking({ open: true, area: activeArea })}><CalendarCheck className="mr-1.5 h-3.5 w-3.5" />Prenota questo tour</Button>
              )}
            </div>
          )}

          <div className="pointer-events-none absolute bottom-5 right-4 z-[6] flex flex-col items-end gap-1.5">
            <span className="flex items-center gap-2 rounded-full bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground shadow backdrop-blur">
              <Compass className="h-3.5 w-3.5 text-primary" />Trascina per orbitare · scroll per zoom
            </span>
            <span className="flex items-center gap-1.5 rounded-full bg-background/70 px-3 py-1 text-[11px] font-medium text-muted-foreground shadow backdrop-blur">
              <Info className="h-3 w-3" />Anteprima Google 3D (Alpha)
            </span>
          </div>
        </>
      )}

      <PoiDialog
        open={poi.open}
        onOpenChange={(o) => setPoi((s) => ({ ...s, open: o }))}
        point={poi.point}
        area={poi.area}
        onBook={() => { const a = poi.area; setPoi((s) => ({ ...s, open: false })); setBooking({ open: true, area: a }); }}
      />
      <BookingDialog open={booking.open} onOpenChange={(o) => setBooking((b) => ({ ...b, open: o }))} area={booking.area} />
    </div>
  );
};

export default SilaWorld3D;
